import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FaCheckCircle,
  FaGlobeAfrica,
  FaMapMarkedAlt,
  FaPhoneAlt,
  FaUserNurse
} from "react-icons/fa";
import heroVideo from "./images/uber.mp4";
import workerVideo from "./images/worker.mp4";

const useUserId = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  return queryParams.get("userId");
};

function Home() {
  const userId = useUserId();

  useEffect(() => {
    if (userId) {
      localStorage.setItem("userID", userId);
    }
  }, [userId]);

  return (
    <div style={page}>
      <style>{responsiveStyles}</style>

      <section style={heroShell}>
        <div style={heroGlow} />

        <div style={heroLayout} className="home-hero-layout">
          <div style={heroCopy}>
            <div style={heroBadge}>Welcome to UberCARE</div>

            <h1 style={heroTitle}>
              UberCARE for Caregivers
            </h1>

            <p style={heroSubtitle}>
              Start here if your caregiver profile has been imported into
              UberCARE. We will guide you to find your profile, unlock it with
              the linked email, and finish the details families need to review.
            </p>

            <div style={signalRow} className="home-signal-row">
              <SignalPill text="A warm start" />
              <SignalPill text="Find your profile" />
              <SignalPill text="Finish your map listing" />
            </div>

            <div style={heroInfoGrid} className="home-hero-info-grid">
              <InfoCard
                title="What to expect first"
                text="Choose Get Started, search for your imported caregiver profile, then use the email attached to that profile to unlock editing."
              />
              <InfoCard
                title="What the demo is for"
                text="The practice map is only a sample. Use it when you want to understand marker colors and map behavior before opening your real profile."
              />
            </div>

            <div style={heroSupportStrip}>
              <div style={supportStripLabel}>Start here</div>
              <p style={supportStripText}>
                Get Started opens the real caregiver workspace. Practice Map is
                only for learning the map before you work on your real profile.
              </p>
            </div>
          </div>

          <div style={heroPanel}>
            <div style={videoGrid} className="home-video-grid">
              <VideoCard
                title="Set your own hourly rate"
                text="You can add your hourly rate so families can view it on your profile."
                videoSrc={heroVideo}
              />

              <VideoCard
                title="Appear on the map"
                text="After completing your profile, you can appear on the map so families can discover where you are available, learn more about your services, and reach out when support is needed."
                videoSrc={workerVideo}
              />
            </div>

            <div style={quickPanel} className="home-quick-panel">
              <div style={quickPanelHeader}>
                <h3 style={quickPanelTitle}>You are in the right place</h3>
                <p style={quickPanelText}>
                  Follow these steps in order. The real profile map is where
                  your actual caregiver details are completed.
                </p>
              </div>

              <StartStep
                number="1"
                title="Open Get Started"
                text="This takes you to the real profile map with imported caregiver profiles."
              />

              <StartStep
                number="2"
                title="Search for your name"
                text="Find your imported profile and open it from the map."
              />

              <StartStep
                number="3"
                title="Unlock and complete it"
                text="Use the linked email to edit your contact details, rate, photo, and experience."
              />

              <div style={mapChoiceReminder}>
                Use the clearly labelled map chooser above whenever you are ready. The gold option
                opens your real profile; the white option is only for practice.
              </div>
            </div>
          </div>
        </div>
      </section>

      <section style={section}>
        <div style={sectionHeader}>
          <p style={sectionEyebrow}>Welcome</p>
          <h2 style={sectionTitle}>Three simple steps to get started</h2>
          <p style={sectionSubtitle}>
            Use Get Started for your real profile. Use Practice Map only when
            you want to learn the map first.
          </p>
        </div>

        <div style={journeyGrid} className="home-journey-grid">
          <StepCard
            icon={<FaUserNurse />}
            number="01"
            title="Claim your imported profile"
            text="Caregivers are imported by the platform, then matched by email from Get Started."
          />

          <StepCard
            icon={<FaCheckCircle />}
            number="02"
            title="Complete your profile"
            text="Add contact information, payment method, hourly rate, profile photo, and experience."
          />

          <StepCard
            icon={<FaPhoneAlt />}
            number="03"
            title="Keep it current"
            text="Return to Get Started whenever you need to update your profile or availability."
          />
        </div>
      </section>

      <section style={supportSection}>
        <div style={supportLayout} className="home-support-layout">
          <div style={supportCopyCard}>
            <p style={sectionEyebrowMuted}>Imported profiles</p>
            <h2 style={supportTitle}>What you can edit from the real map</h2>
            <p style={supportText}>
              Imported caregivers can confirm the profile email and continue
              editing their details from the real map.
            </p>

            <div style={benefitList}>
              <BenefitRow
                icon={<FaMapMarkedAlt />}
                title="Map presence"
                text="Your map presence helps families discover you, view your details, and understand when you may be available to help."
              />
              <BenefitRow
                icon={<FaGlobeAfrica />}
                title="Profile details"
                text="Your map profile can include contact details, payment information, location, and hourly rate for families to review."
              />
            </div>
          </div>

          <div style={supportAsideCard}>
            <div style={asideBadge}>Best next action</div>
            <h3 style={asideTitle}>Already imported? Continue from the map.</h3>
            <p style={asideText}>
              Choose Get Started, find your profile, and use the linked email
              address to edit missing details.
            </p>

            <a href="#caregiver-map-choices" style={asideButton}>
              Return to the map choices
            </a>
          </div>
        </div>
      </section>

      <section style={ctaSection}>
        <div style={ctaCard} className="home-cta-card">
          <div style={ctaCopy}>
            <div style={ctaBadge}>Ready to begin</div>
            <h2 style={ctaTitle}>We are glad you are here.</h2>
            <p style={ctaText}>
              When you are ready, open the real map to complete your imported
              caregiver profile.
            </p>
          </div>

          <a href="#caregiver-map-choices" style={ctaPrimary}>
            Choose your map above
          </a>
        </div>
      </section>

      <section style={footerSection}>
        <Link to="/caregiver-marketing" style={footerLink}>
          View the caregiver marketing page
        </Link>
      </section>
    </div>
  );
}

function SignalPill({ text }) {
  return <div style={signalPill}>{text}</div>;
}

function InfoCard({ title, text }) {
  return (
    <div style={infoCard}>
      <h4 style={infoTitle}>{title}</h4>
      <p style={infoText}>{text}</p>
    </div>
  );
}

function VideoCard({ title, text, videoSrc }) {
  return (
    <div style={videoCard}>
      <div style={mediaHeader}>
        <span style={mediaTag}>Preview</span>
        <span style={mediaNote}>Caregiver experience</span>
      </div>

      <div style={videoFrame}>
        <video
          style={videoStyle}
          controls
          autoPlay
          muted
          loop
          playsInline
        >
          <source src={videoSrc} type="video/mp4" />
        </video>
      </div>

      <div style={videoCopy}>
        <h4 style={videoTitle}>{title}</h4>
        <p style={videoText}>{text}</p>
      </div>
    </div>
  );
}

function StartStep({ number, title, text }) {
  return (
    <div style={startStep}>
      <div style={startStepNumber}>{number}</div>
      <div>
        <h4 style={quickActionTitle}>{title}</h4>
        <p style={quickActionText}>{text}</p>
      </div>
    </div>
  );
}

function StepCard({ number, title, text, icon }) {
  return (
    <div style={stepCard}>
      <div style={stepIcon}>{icon}</div>
      <div style={stepNumber}>{number}</div>
      <h3 style={stepTitle}>{title}</h3>
      <p style={stepText}>{text}</p>
    </div>
  );
}

function BenefitRow({ icon, title, text }) {
  return (
    <div style={benefitRow}>
      <div style={benefitIcon}>{icon}</div>
      <div>
        <h3 style={benefitTitle}>{title}</h3>
        <p style={benefitText}>{text}</p>
      </div>
    </div>
  );
}

const responsiveStyles = `
  .home-hero-layout {
    grid-template-columns: minmax(0, 1.05fr) minmax(320px, 0.95fr);
  }

  .home-support-layout {
    grid-template-columns: minmax(0, 1.1fr) minmax(300px, 0.9fr);
  }

  .home-journey-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .home-video-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .home-hero-info-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 1040px) {
    .home-hero-layout {
      grid-template-columns: 1fr !important;
    }

    .home-support-layout {
      grid-template-columns: 1fr !important;
    }

    .home-cta-card {
      grid-template-columns: 1fr !important;
    }

    .home-video-grid {
      grid-template-columns: 1fr !important;
    }

    .home-hero-info-grid {
      grid-template-columns: 1fr !important;
    }
  }

  @media (max-width: 820px) {
    .home-journey-grid {
      grid-template-columns: 1fr !important;
    }
  }

  @media (max-width: 760px) {
    .home-hero-layout,
    .home-support-layout,
    .home-journey-grid,
    .home-video-grid,
    .home-hero-info-grid {
      gap: 14px !important;
    }

    .home-signal-row {
      gap: 10px !important;
    }

    .home-hero-buttons,
    .home-cta-actions {
      display: grid !important;
      grid-template-columns: 1fr !important;
      width: 100% !important;
    }

    .home-hero-buttons button,
    .home-cta-actions button,
    .home-quick-panel button {
      width: 100% !important;
      justify-content: center !important;
      min-height: 48px !important;
    }

    .home-quick-panel {
      border-radius: 20px !important;
    }
  }

  @media (max-width: 420px) {
    .home-signal-row > * {
      width: 100% !important;
      justify-content: center !important;
    }
  }
`;

const page = {
  minHeight: "100vh",
  background: "linear-gradient(180deg, #eef4f7 0%, #e7eef2 100%)",
  color: "#123a59"
};

const heroShell = {
  position: "relative",
  overflow: "hidden",
  padding: "0 22px 28px",
  background:
    "radial-gradient(circle at 18% 16%, rgba(241, 196, 15, 0.16), transparent 22%), radial-gradient(circle at 84% 20%, rgba(31, 98, 143, 0.12), transparent 20%), linear-gradient(180deg, #f7fbfc 0%, #eef5f7 100%)"
};

const heroGlow = {
  position: "absolute",
  inset: 0,
  background:
    "linear-gradient(180deg, rgba(255, 255, 255, 0.2), rgba(232, 240, 244, 0.4))",
  pointerEvents: "none"
};

const heroLayout = {
  position: "relative",
  zIndex: 1,
  maxWidth: "1320px",
  margin: "0 auto",
  display: "grid",
  gap: "28px",
  alignItems: "start"
};

const heroCopy = {
  minWidth: 0,
  paddingTop: "0",
  alignSelf: "start"
};

const heroBadge = {
  display: "inline-flex",
  alignItems: "center",
  padding: "8px 14px",
  borderRadius: "999px",
  background: "rgba(241, 196, 15, 0.16)",
  border: "1px solid rgba(241, 196, 15, 0.24)",
  color: "#174d73",
  fontSize: "12px",
  fontWeight: "800",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  marginBottom: "18px"
};

const heroTitle = {
  margin: 0,
  fontSize: "clamp(2.1rem, 4.5vw, 3.75rem)",
  lineHeight: 1.02,
  letterSpacing: 0,
  color: "#164f73"
};

const heroSubtitle = {
  margin: "16px 0 0",
  maxWidth: "680px",
  fontSize: "clamp(0.98rem, 1.7vw, 1.2rem)",
  lineHeight: 1.6,
  color: "#47657d"
};

const signalRow = {
  marginTop: "16px",
  display: "flex",
  gap: "12px",
  flexWrap: "wrap"
};

const signalPill = {
  display: "inline-flex",
  alignItems: "center",
  padding: "10px 14px",
  borderRadius: "999px",
  background: "rgba(255, 255, 255, 0.88)",
  border: "1px solid rgba(20, 90, 131, 0.08)",
  color: "#4e697f",
  fontSize: "13px",
  fontWeight: "700"
};

const heroInfoGrid = {
  marginTop: "18px",
  display: "grid",
  gap: "14px"
};

const infoCard = {
  padding: "16px",
  borderRadius: "20px",
  background: "rgba(255, 255, 255, 0.82)",
  border: "1px solid rgba(20, 90, 131, 0.08)",
  boxShadow: "0 12px 26px rgba(12, 42, 68, 0.05)"
};

const infoTitle = {
  margin: "0 0 8px",
  color: "#164f73",
  fontSize: "18px",
  lineHeight: 1.3
};

const infoText = {
  margin: 0,
  color: "#58718a",
  fontSize: "15px",
  lineHeight: 1.65
};

const heroSupportStrip = {
  marginTop: "16px",
  padding: "18px 20px",
  borderRadius: "22px",
  background: "linear-gradient(135deg, rgba(31, 98, 143, 0.08), rgba(241, 196, 15, 0.14))",
  border: "1px solid rgba(20, 90, 131, 0.08)"
};

const supportStripLabel = {
  marginBottom: "8px",
  color: "#1f628f",
  fontSize: "12px",
  fontWeight: "800",
  letterSpacing: "0.08em",
  textTransform: "uppercase"
};

const supportStripText = {
  margin: 0,
  color: "#47657d",
  fontSize: "15px",
  lineHeight: 1.7
};

const heroPanel = {
  minWidth: 0,
  display: "grid",
  gap: "16px",
  alignSelf: "start"
};

const videoGrid = {
  display: "grid",
  gap: "16px"
};

const videoCard = {
  padding: "14px",
  borderRadius: "24px",
  background: "rgba(255, 255, 255, 0.9)",
  border: "1px solid rgba(20, 90, 131, 0.08)",
  boxShadow: "0 18px 38px rgba(12, 42, 68, 0.08)"
};

const mediaHeader = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "12px",
  marginBottom: "12px"
};

const mediaTag = {
  display: "inline-flex",
  padding: "6px 10px",
  borderRadius: "999px",
  background: "rgba(241, 196, 15, 0.16)",
  color: "#174d73",
  fontSize: "11px",
  fontWeight: "800",
  textTransform: "uppercase",
  letterSpacing: "0.08em"
};

const mediaNote = {
  color: "#6a8194",
  fontSize: "12px",
  fontWeight: "700"
};

const videoFrame = {
  borderRadius: "22px",
  overflow: "hidden",
  background: "#dce8ef",
  border: "1px solid rgba(20, 90, 131, 0.08)"
};

const videoStyle = {
  width: "100%",
  display: "block",
  aspectRatio: "10 / 13",
  objectFit: "cover",
  background: "#dce8ef"
};

const videoCopy = {
  marginTop: "12px"
};

const videoTitle = {
  margin: 0,
  color: "#164f73",
  fontSize: "19px",
  lineHeight: 1.3
};

const videoText = {
  margin: "8px 0 0",
  color: "#58718a",
  fontSize: "15px",
  lineHeight: 1.6
};

const quickPanel = {
  padding: "18px",
  borderRadius: "24px",
  background: "#164f73",
  color: "#ffffff",
  display: "grid",
  gap: "14px",
  boxShadow: "0 18px 36px rgba(12, 42, 68, 0.12)"
};

const quickPanelHeader = {
  marginBottom: "2px"
};

const quickPanelTitle = {
  margin: "0 0 8px",
  fontSize: "20px"
};

const quickPanelText = {
  margin: 0,
  color: "rgba(255, 255, 255, 0.82)",
  lineHeight: 1.6,
  fontSize: "13px"
};

const mapChoiceReminder = {
  padding: "16px",
  borderRadius: "18px",
  background: "rgba(241, 196, 15, 0.14)",
  border: "1px solid rgba(241, 196, 15, 0.28)",
  color: "rgba(255, 255, 255, 0.9)",
  fontSize: "13px",
  fontWeight: "700",
  lineHeight: 1.6
};

const startStep = {
  display: "grid",
  gridTemplateColumns: "42px minmax(0, 1fr)",
  gap: "12px",
  alignItems: "start",
  padding: "14px",
  borderRadius: "18px",
  background: "rgba(255, 255, 255, 0.08)",
  border: "1px solid rgba(255, 255, 255, 0.1)"
};

const startStepNumber = {
  width: "42px",
  height: "42px",
  borderRadius: "14px",
  background: "#ffd24a",
  color: "#14324c",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "16px",
  fontWeight: "900"
};

const quickActionTitle = {
  margin: "0 0 6px",
  fontSize: "17px",
  color: "#ffffff"
};

const quickActionText = {
  margin: 0,
  color: "rgba(255, 255, 255, 0.78)",
  lineHeight: 1.6,
  fontSize: "13px"
};

const section = {
  padding: "36px 22px",
  background: "linear-gradient(180deg, #f2f7fa 0%, #edf3f6 100%)"
};

const sectionHeader = {
  maxWidth: "760px",
  margin: "0 auto 30px",
  textAlign: "center"
};

const sectionEyebrow = {
  margin: "0 0 12px",
  color: "#1f628f",
  fontSize: "11px",
  fontWeight: "800",
  textTransform: "uppercase",
  letterSpacing: "0.09em"
};

const sectionEyebrowMuted = {
  ...sectionEyebrow,
  color: "#f9e48f"
};

const sectionTitle = {
  margin: 0,
  color: "#164f73",
  fontSize: "clamp(1.65rem, 2.6vw, 2.2rem)",
  lineHeight: 1.12
};

const sectionSubtitle = {
  margin: "14px 0 0",
  fontSize: "15px",
  color: "#557087",
  lineHeight: 1.7
};

const journeyGrid = {
  maxWidth: "1320px",
  margin: "0 auto",
  display: "grid",
  gap: "18px"
};

const stepCard = {
  padding: "24px",
  background: "rgba(255, 255, 255, 0.94)",
  borderRadius: "24px",
  boxShadow: "0 14px 28px rgba(8, 30, 52, 0.06)",
  border: "1px solid rgba(20, 90, 131, 0.08)"
};

const stepIcon = {
  width: "50px",
  height: "50px",
  borderRadius: "16px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(241, 196, 15, 0.16)",
  color: "#174d73",
  fontSize: "20px",
  marginBottom: "14px"
};

const stepNumber = {
  fontSize: "11px",
  fontWeight: "800",
  color: "#5f8aa8",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  marginBottom: "10px"
};

const stepTitle = {
  margin: "0 0 10px",
  color: "#164f73",
  fontSize: "18px",
  lineHeight: 1.25
};

const stepText = {
  margin: 0,
  color: "#546e84",
  fontSize: "14px",
  lineHeight: 1.65
};

const supportSection = {
  padding: "0 22px 36px",
  background: "linear-gradient(180deg, #edf3f6 0%, #e6eef2 100%)"
};

const supportLayout = {
  maxWidth: "1320px",
  margin: "0 auto",
  display: "grid",
  gap: "18px"
};

const supportCopyCard = {
  padding: "28px",
  borderRadius: "28px",
  background: "linear-gradient(180deg, #164f73 0%, #1c6284 100%)",
  color: "#ffffff",
  boxShadow: "0 18px 38px rgba(12, 42, 68, 0.12)"
};

const supportTitle = {
  margin: "0 0 12px",
  fontSize: "clamp(1.65rem, 2.7vw, 2.15rem)",
  lineHeight: 1.15
};

const supportText = {
  margin: 0,
  maxWidth: "700px",
  color: "rgba(255, 255, 255, 0.82)",
  fontSize: "15px",
  lineHeight: 1.7
};

const benefitList = {
  display: "grid",
  gap: "14px",
  marginTop: "22px"
};

const benefitRow = {
  display: "grid",
  gridTemplateColumns: "54px minmax(0, 1fr)",
  gap: "14px",
  alignItems: "start",
  padding: "16px",
  borderRadius: "20px",
  background: "rgba(255, 255, 255, 0.08)",
  border: "1px solid rgba(255, 255, 255, 0.1)"
};

const benefitIcon = {
  width: "54px",
  height: "54px",
  borderRadius: "16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(241, 196, 15, 0.18)",
  color: "#f9e48f",
  fontSize: "20px"
};

const benefitTitle = {
  margin: "0 0 6px",
  color: "#ffffff",
  fontSize: "17px"
};

const benefitText = {
  margin: 0,
  color: "rgba(255, 255, 255, 0.78)",
  fontSize: "13px",
  lineHeight: 1.65
};

const supportAsideCard = {
  padding: "26px",
  borderRadius: "28px",
  background: "rgba(255, 255, 255, 0.94)",
  border: "1px solid rgba(20, 90, 131, 0.08)",
  boxShadow: "0 16px 34px rgba(12, 42, 68, 0.08)",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center"
};

const asideBadge = {
  display: "inline-flex",
  alignSelf: "flex-start",
  padding: "8px 12px",
  borderRadius: "999px",
  background: "rgba(241, 196, 15, 0.16)",
  color: "#174d73",
  fontSize: "11px",
  fontWeight: "800",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  marginBottom: "16px"
};

const asideTitle = {
  margin: "0 0 12px",
  color: "#164f73",
  fontSize: "22px",
  lineHeight: 1.3
};

const asideText = {
  margin: 0,
  color: "#58718a",
  fontSize: "14px",
  lineHeight: 1.7
};

const asideButton = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "fit-content",
  marginTop: "20px",
  padding: "13px 18px",
  borderRadius: "16px",
  border: "none",
  background: "linear-gradient(135deg, #145a83 0%, #1f7198 100%)",
  color: "#ffffff",
  fontSize: "14px",
  fontWeight: "800",
  cursor: "pointer",
  textDecoration: "none"
};

const ctaSection = {
  padding: "0 22px 42px",
  background: "linear-gradient(180deg, #e6eef2 0%, #e2ebf0 100%)"
};

const footerSection = {
  padding: "0 22px 48px",
  background: "linear-gradient(180deg, #e2ebf0 0%, #dfe9ee 100%)",
  textAlign: "center"
};

const footerLink = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "14px 22px",
  borderRadius: "999px",
  textDecoration: "none",
  background: "rgba(255, 255, 255, 0.9)",
  border: "1px solid rgba(20, 90, 131, 0.1)",
  color: "#164f73",
  fontSize: "14px",
  fontWeight: "800",
  boxShadow: "0 12px 26px rgba(12, 42, 68, 0.06)"
};

const ctaCard = {
  maxWidth: "1320px",
  margin: "0 auto",
  padding: "24px",
  borderRadius: "30px",
  background: "linear-gradient(135deg, #f9fcfd 0%, #eef5f7 100%)",
  border: "1px solid rgba(20, 90, 131, 0.08)",
  boxShadow: "0 18px 34px rgba(12, 42, 68, 0.08)",
  display: "grid",
  gridTemplateColumns: "minmax(0, 1.2fr) minmax(260px, 0.8fr)",
  gap: "24px",
  alignItems: "center"
};

const ctaCopy = {
  minWidth: 0
};

const ctaBadge = {
  display: "inline-flex",
  padding: "8px 14px",
  borderRadius: "999px",
  background: "rgba(241, 196, 15, 0.16)",
  color: "#174d73",
  fontSize: "11px",
  fontWeight: "800",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  marginBottom: "16px"
};

const ctaTitle = {
  margin: "0 0 12px",
  color: "#164f73",
  fontSize: "clamp(1.7rem, 2.8vw, 2.2rem)",
  lineHeight: 1.12
};

const ctaText = {
  margin: 0,
  color: "#58718a",
  fontSize: "15px",
  lineHeight: 1.7
};

const ctaPrimary = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "13px 18px",
  borderRadius: "16px",
  border: "1px solid #e5b900",
  background: "linear-gradient(135deg, #ffd33d, #f1c40f)",
  color: "#17324a",
  fontSize: "15px",
  fontWeight: "800",
  cursor: "pointer",
  textDecoration: "none"
};

export default Home;
